<template>
  <div>
    <b-card>
      <b-row>
        <!-- Global Search -->
        <b-col cols="12" md="3" class="mt-auto">
          <label for="globalSearch">Global Search</label>
          <b-form-input
            id="globalSearch"
            v-model="filters.search"
            placeholder="Search any field: name, email, username, txn id, method, type, status, notes…"
          />
        </b-col>
        <b-col cols="9">
          <b-row>
            <b-col cols="12" md="2">
              <label for="dateRange">Date Range</label>
              <b-form-select
                id="dateRange"
                v-model="filters.dateRange"
                :options="dateRanges"
              />
            </b-col>
            <b-col cols="6" md="2">
              <label for="dateFrom">Date From</label>
              <flat-pickr
                v-model="filters.startDate"
                id="dateFrom"
                class="form-control"
                :config="configFrom"
                placeholder="Date From"
                @input="(val) => (this.filters.startDate = val)"
              />
            </b-col>

            <b-col cols="6" md="2">
              <label for="dateTo">Date To</label>
              <flat-pickr
                v-model="filters.endDate"
                id="dateTo"
                class="form-control"
                :config="configTo"
                placeholder="Date To"
                @input="(val) => (this.filters.endDate = val)"
              />
            </b-col>
            <b-col cols="6" md="2">
              <label for="status">Status</label>
              <b-form-select
                id="status"
                v-model="filters.status"
                :options="statusOptions"
              />
            </b-col>

            <!-- Transaction Type -->
            <b-col cols="6" md="2">
              <label for="method">Transaction Type</label>
              <b-form-select
                id="method"
                v-model="filters.method"
                :options="methodOptions"
              />
            </b-col>

            <!-- Type -->
            <b-col cols="6" md="2">
              <label for="type">Type</label>
              <b-form-select
                id="type"
                v-model="filters.type"
                :options="typeOptions"
              />
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col cols="6" md="2">
              <label for="accountingStatus">Accounting Status</label>
              <b-form-select
                id="accountingStatus"
                v-model="filters.accountingStatus"
                :options="accountingStatusOptions"
              />
            </b-col>
            <!-- Min/Max Amount -->
            <b-col cols="6" md="2">
              <label for="minAmount">Min Amount</label>
              <b-form-input
                id="minAmount"
                v-model="filters.minAmount"
                type="number"
                placeholder="0.00"
              />
            </b-col>
            <b-col cols="6" md="2">
              <label for="maxAmount">Max Amount</label>
              <b-form-input
                id="maxAmount"
                v-model="filters.maxAmount"
                type="number"
                placeholder="9999.99"
              />
            </b-col>
            <!-- Status -->

            <!-- Processor -->
            <b-col cols="6" md="2">
              <label for="processor">Processor</label>
              <b-form-select
                id="processor"
                v-model="filters.processor"
                :options="processorOptions"
              />
            </b-col>

            <!-- Source -->
            <b-col cols="6" md="2">
              <label for="source">Source</label>
              <b-form-select
                id="source"
                v-model="filters.source"
                :options="sourceOptions"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <!-- Action buttons -->
      <div class="d-flex justify-content-end mt-1" style="gap: 8px">
        <b-button variant="outline-primary" @click="handleExportCSV">Export CSV</b-button>
        <b-button variant="outline-secondary" @click="clearFilters"
          >Clear</b-button
        >
        <b-button variant="primary">Compact</b-button>
      </div>
    </b-card>

    <!-- Connect Buttons -->
    <b-button-group class="mb-2 float-right">
      <b-button variant="outline-primary" @click="handleOpenModal"
        >MYOB Connect</b-button
      >
      <b-button variant="outline-secondary" @click="handleOpenModal"
        >Xero Connect</b-button
      >
      <b-button variant="outline-success" @click="handleOpenModal"
        >QuickBooks Connect</b-button
      >
      <b-button variant="success" @click="handleOpenAddTrans"
        >+ Add Transaction</b-button
      >
    </b-button-group>

    <!-- Transactions Table -->
    <b-table
      :key="tableKey"
      :items="items"
      :fields="fields"
      responsive
      @row-clicked="toggleDetails"
      thead-class="text-nowrap"
      :busy="loadingTrans"
      show-empty
    >
      <template #empty>
        <div class="text-center text-dark my-2">No matching records found</div>
      </template>
      <template #cell(createdTime)="{ item }">
        <span>
          {{ format(new Date(item.createdTime), "yyyy-MM-dd HH:mm") }}
        </span>
      </template>
      <template #cell(id)="data">
        <b-link @click.stop="toggleDetails(data.item)">
          <feather-icon icon="ChevronDownIcon" :class="itemClass(data.item)" />
          {{ data.item.id }}
        </b-link>
      </template>
      <template #cell(recipient)="{ item }">
        <span>
          {{ item.recipient || "-" }}
        </span>
      </template>
      <template #cell(username)="{ item }">
        <span>
          {{ item.username }}
        </span>
      </template>

      <template #cell(processor)="{ item }">
        <span>
          {{ item.processor || "-" }}
        </span>
      </template>
      <template #cell(method)="{ item }">
        <span>
          {{ item.transactionableType || "-" }}
        </span>
      </template>
      <template #cell(type)="{ item }">
        <span>
          {{ formatType(item.type) }}
        </span>
      </template>
      <template #cell(amount)="{ item }">
        <span>
          {{ item.amount ? formatCurrencyBuiltInFunc(item.amount) : "N/A" }}
        </span>
      </template>
      <template #cell(bonusCash)="{ item }">
        <span>
          {{ item.bonus ? formatCurrencyBuiltInFunc(item.bonus) : "N/A" }}
        </span>
      </template>
      <template #cell(status)="{ item }">
        <span>
          {{ item.status || "-" }}
        </span>
      </template>
      <template #cell(source)="{ item }">
        <span>
          {{ item.source || "-" }}
        </span>
      </template>
      <template #cell(accounting)="{ item }">
        <b-badge variant="secondary">{{ item.accounting || "-" }}</b-badge>
      </template>

      <template #cell(notes)="{ item }">
        <span>
          {{ item.description || "-" }}
        </span>
      </template>

      <!-- Expanded row -->
      <template #row-details="row">
        <b-card>
          <b-row v-if="row.item">
            <b-col><strong>Bet ID:</strong> {{ "-" }}</b-col>
            <b-col
              ><strong>Selection:</strong>
              {{ row.item.selection || "-" }}</b-col
            >
            <b-col><strong>Market:</strong> {{ row.item.market || "-" }}</b-col>
            <b-col
              ><strong>Stake:</strong>
              {{ formatCurrencyBuiltInFunc(row.item.stake) || "-" }}</b-col
            >
            <b-col
              ><strong>Price:</strong>
              {{ formatCurrencyBuiltInFunc(row.item.price) || "-" }}</b-col
            >
            <b-col><strong>Result:</strong> {{ row.item.result || "-" }}</b-col>
          </b-row>
          <b-row v-else>
            <b-col cols="12">
              <span>No additional details</span>
            </b-col>
          </b-row>
        </b-card>
      </template>
    </b-table>
    <div class="d-flex flex-wrap justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <span class="mr-1 text-nowrap">Rows per page</span>
        <b-form-select
          v-model="limit"
          :options="limitOptions"
          size="sm"
          class="mr-1"
        />
        <span class="text-nowrap">
          {{ paginationSummary }}
        </span>
      </div>

      <b-pagination
        v-model="page"
        :total-rows="paginationTrans.total"
        :per-page="limit"
        first-number
        last-number
      />
    </div>

    <!-- Modal -->
    <b-modal
      id="xero-modal"
      size="lg"
      centered
      scrollable
      header-bg-variant="primary"
      header-text-variant="light"
      body-class="modal-body-scrollable"
    >
      <template #modal-header>
        <h5 class="modal-title text-white">Xero Connect</h5>
        <div class="modal-actions">
          <feather-icon icon="XIcon" class="cursor-pointer" @click="close" />
        </div>
      </template>
      <b-container fluid class="text-black">
        <p class="text-body">
          Select transactions to send to your Xero system. Choose an accounting
          type for each row, then click "Send Selected". Rows marked as Sent
          will be skipped next time. Archived or Merged duplicates are excluded.
        </p>

        <div class="mb-2 text-body">
          <strong>Duplicates:</strong> 0 | <strong>Sendable:</strong> 50
        </div>

        <!-- Table -->
        <b-table small :items="transactions" :fields="fieldsModal" responsive>
          <template #head(select)>
            <b-form-checkbox
              v-model="selectAll"
              @change="toggleSelectAll"
            ></b-form-checkbox>
          </template>
          <!-- custom column checkbox -->
          <template #cell(select)="row">
            <b-form-checkbox
              :checked="selected.includes(row.item.id)"
              @change="checkSelectItem(row.item)"
            />
          </template>

          <template #cell(txnId)="row">
            <span>{{ row.item.id }}</span>
          </template>
          <template #cell(date)="row">
            <span>{{
              format(new Date(row.item.createdTime), "yyyy-MM-dd hh:mm")
            }}</span>
          </template>
          <!-- dropdown cho Accounting Type -->
          <template #cell(accountingType)="row">
            <b-form-select
              v-model="row.item.accountingType"
              :options="accountingTypes"
              size="sm"
            />
          </template>

          <template #cell(customer)="row">
            <span>{{ row.item.createdBy }}</span>
          </template>
          <template #cell(type)="row">
            <span>{{ row.item.transactionableType }}</span>
          </template>

          <!-- badge cho Status -->
          <template #cell(status)="row">
            <b-badge variant="secondary">{{ row.item.status }}</b-badge>
          </template>
        </b-table>
      </b-container>

      <template #modal-footer>
        <b-row class="w-100">
          <div
            class="d-flex align-items-center justify-content-end ml-auto"
            style="gap: 8px"
          >
            <b-button variant="outline-secondary" class="w-100" @click="close"
              >Close</b-button
            >
            <b-button variant="primary" class="w-100 text-nowrap"
              >Send Selected</b-button
            >
          </div>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCol,
  BFormDatepicker,
  BFormInput,
  BFormSelect,
  BRow,
  BTable,
  BLink,
  BButtonGroup,
  BCard,
  BPagination,
  BModal,
  BFormCheckbox,
  BFormGroup,
  BFormTextarea,
  BContainer,
} from "bootstrap-vue";
import { formatCurrencyBuiltInFunc, formatDate } from "@/helpers/utils";
import ABA from "aba-generator";
import { mapActions, mapState } from "vuex";
import flatPickr from "vue-flatpickr-component";
import { debounce } from "debounce";
import { subDays, startOfToday, format } from "date-fns";

export default {
  components: {
    BButton,
    BFormInput,
    BFormSelect,
    BFormDatepicker,
    BTable,
    BBadge,
    BRow,
    BCol,
    BLink,
    BButtonGroup,
    BCard,
    BPagination,
    BModal,
    BFormCheckbox,
    BFormGroup,
    BFormTextarea,
    flatPickr,
    BContainer,
  },
  data() {
    return {
      selectAll: false,
      selected: [],
      tableKey: 0,
      page: 1,
      configFrom: {
        altFormat: "d-m-Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      configTo: {
        altFormat: "d-m-Y",
        altInput: true,
        dateFormat: "Y-m-d",
        minDate: null,
      },
      filters: {
        search: "",
        dateRange: "Custom",
        startDate: null,
        endDate: null,
        minAmount: null,
        maxAmount: null,
        status: "All",
        method: "All",
        type: "All",
        accountingStatus: "All",
        processor: "All",
        source: "All",
      },
      page: 1,
      limit: 25,
      limitOptions: [10, 25, 50, 100],
      dateRanges: ["Custom", "Today", "Last 7 days", "Last 30 days"],
      statusOptions: [
        "All",
        "Approved",
        "Pending",
        "Failed",
        "Cancelled",
        "Reversed",
        "Flagged",
      ],
      methodOptions: ["All", "Card", "Bank Transfer", "PayID", "Cash", "Bonus"],
      typeOptions: [
        "All",
        "Deposit",
        "Bet-Refund",
        "Withdrawal",
        "Bet-Win",
        "Bet-Entry",
      ],
      accountingStatusOptions: [
        "All",
        "Sent",
        "Not Sent",
        "Archived",
        "Merged",
      ],
      processorOptions: ["All", "StakeToken", "Stripe", "Adyen", "Bank"],
      sourceOptions: ["All", "Website", "Mobile App", "Admin", "API"],

      fields: [
        { key: "createdTime", label: "Transaction Date" },
        { key: "amount", label: "Amount" },
        { key: "bonusCash", label: "Bonus Cash" },
        { key: "username", label: "Username" },
        { key: "id", label: "Transaction ID" },
        { key: "recipient", label: "Recipient Name" },
        { key: "processor", label: "Processor" },
        { key: "type", label: "Type" },
        { key: "method", label: "Transaction Type" },
        { key: "status", label: "Status" },
        { key: "source", label: "Source" },
        { key: "accounting", label: "Accounting" },
        { key: "notes", label: "Notes" },
      ],
      items: [],
      fieldsModal: [
        { key: "select", label: "" },
        { key: "txnId", label: "Txn ID" },
        { key: "date", label: "Date" },
        { key: "customer", label: "Customer" },
        { key: "type", label: "Type" },
        { key: "method", label: "Method" },
        { key: "amount", label: "Amount" },
        {
          key: "accountingType",
          label:
            "Accounting Type (Sale / Payment / Refund / Journal / Adjustment)",
        },
        { key: "status", label: "Status" },
      ],
      accountingTypes: ["Payment", "Refund", "Journal", "Adjustment"],
    };
  },
  created() {
    this.fetchTransactions();
  },
  computed: {
    ...mapState("finance/transactions", [
      "loadingTrans",
      "transactions",
      "paginationTrans",
    ]),
    paginationSummary() {
      const p = this.paginationTrans || {};
      const page = Number(p.page) || 1;
      const limit = Number(p.limit || p.perPage) || 10;
      const total = Number(p.total) || 0;

      const from = total === 0 ? 0 : (page - 1) * limit + 1;
      const to = total === 0 ? 0 : Math.min(page * limit, total);

      return total === 0
        ? `Showing 0 of 0 entries`
        : `Showing ${from} - ${to} of ${total} entries`;
    },
  },
  watch: {
    "filters.search"() {
      this.handleSearchDebounced();
    },
    page() {
      this.fetchTransactions();
    },
    limit() {
      this.fetchTransactions();
    },
    "filters.startDate"(newVal) {
      this.configTo = {
        ...this.configTo,
        minDate: newVal || null,
      };
      this.fetchTransactions();
    },

    "filters.endDate"(newVal) {
      this.configFrom = {
        ...this.configFrom,
        maxDate: newVal || null,
      };
      this.fetchTransactions();
    },
    "filters.dateRange"(val) {
      const fmt = (date) => format(date, "yyyy-MM-dd");

      if (val === "custom") {
        this.filters.startDate = "";
        this.filters.endDate = "";
        this.fetchTransactions();
        return;
      }

      const endDate = startOfToday();
      const daysMap = {
        today: 0,
        "7days": 7,
        "30days": 30,
        "90days": 90,
      };

      const days = daysMap[val];
      if (days !== undefined) {
        const startDate = subDays(endDate, days);
        this.filters.startDate = fmt(startDate);
        this.filters.endDate = fmt(endDate);
      }
      this.fetchTransactions();
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  methods: {
    formatCurrencyBuiltInFunc,
    formatType(value) {
      if (!value) return "-";
      const typeMap = {
        deposit: "Deposit",
        withdrawal: "Withdrawal",
        bettip: "Bet-Entry",
        betWin: "Bet-Win",
        betRefund: "Bet-Refund",
        bonusOffer: "Bonus Offer",
      };
      return typeMap[value] || value;
    },
    clearFilters() {
      this.filters = {
        search: "",
        dateRange: "Custom",
        startDate: null,
        startDate: null,
        minAmount: null,
        maxAmount: null,
        status: "All",
        method: "All",
        type: "All",
        accountingStatus: "All",
        processor: "All",
        source: "All",
      };
    },
    toggleDetails(item) {
      this.items = this.items.map((t) => ({
        ...t,
        _showDetails: t.id === item.id ? !t._showDetails : false,
      }));
    },
    itemClass(item) {
      return item._showDetails ? "rotate-180" : "rotate-0";
    },
    toggleSelectAll(value) {
      this.selectAll = value;
      this.selected = value ? this.transactions.map((i) => i.id) : [];
    },
    checkSelectItem(item) {
      if (this.selected.includes(item.id)) {
        this.selected = this.selected.filter((id) => id !== item.id);
      } else {
        this.selected.push(item.id);
      }
      this.selectAll = this.selected.length === this.transactions.length;
    },
    handleOpenModal() {
      this.$bvModal.show("xero-modal");
    },
    handleOpenAddTrans() {
      this.$bvModal.show("add-transaction-modal");
    },
    ...mapActions({
      getTransactions: "finance/transactions/getTransactions",
    }),
    format,
    formatCurrencyBuiltInFunc,
    async fetchTransactions() {
      if (this.loadingTrans) return;
      try {
        const params = {
          ...(!!this.filters.startDate && {
            "date[startDate]": this.filters.startDate,
          }),
          ...(!!this.filters.endDate && {
            "date[endDate]": this.filters.endDate,
          }),
          ...(this.filters.search && { search: this.filters.search }),
          page: this.page,
          limit: this.limit,
        };

        const data = await this.getTransactions(params);
        this.items = data.map((item) => ({
          ...item,
          _showDetails: false,
        }));
      } catch (e) {
        this.items = [];
      }
    },
    updateTableKey() {
      this.tableKey++;
    },
    handleSearch() {
      this.page = 1;
      this.fetchTransactions();
    },
    close() {
      this.$bvModal.hide("xero-modal");
    },
    downloadFile(content, filename) {
      const blob = new Blob([content], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    handleExportABA() {
      if (!this.items.length) return;
      const pays = this.items
        .filter((item) => item.bsb && item.accountNumber)
        .map((item) => ({
          bsb: item.bsb,
          transactionCode: "50",
          account: (item.accountNumber || "").replace(/\s/g, ""),
          amount: item.amount ? +Number(item.amount).toFixed(2) : 0,
          accountTitle: item.recipient || item.username || "",
          reference: "StakeToken",
          traceBsb: "084-472",
          traceAccount: "738301686",
          remitter: "StakeToken",
        }));

      if (!pays.length) return;

      const totalAmount = pays.reduce((acc, p) => acc + p.amount, 0);
      const aba = new ABA({
        header: {
          bank: "NAB",
          user: "StakeToken Trading",
          userNumber: "000000",
          description: "StakeToken",
        },
        footer: {
          netTotal: "0000000000",
          debitTotal: +totalAmount.toFixed(2),
        },
      });
      const file = aba.generate(pays);
      const date = formatDate(new Date(), "yyyyMMdd_HHmm");
      this.downloadFile(file, `TransactionsStakeToken_${date}.aba`);
    },
    handleExportCSV() {
      if (!this.items.length) return;

      const headers = [
        "Transaction Date",
        "Transaction Time",
        "Transaction ID",
        "Username",
        "Recipient Name",
        "Processor",
        "Type",
        "Transaction Type",
        "Amount",
        "Bonus Cash",
        "Status",
        "Source",
        "Accounting",
        "Notes",
      ];

      const rows = this.items.map((item) => {
        const dateObj = item.createdTime ? new Date(item.createdTime) : null;
        return [
          dateObj ? this.format(dateObj, "yyyy-MM-dd") : "",
          dateObj ? this.format(dateObj, "HH:mm:ss") : "",
          item.id || "",
          item.username || "",
          item.recipient || "",
          item.processor || "",
          this.formatType(item.type),
          item.method || "",
          item.amount || "",
          item.bonus || "",
          item.status || "",
          item.source || "",
          item.accounting || "",
          item.description || "",
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",");
      });

      const csv = [headers.join(","), ...rows].join("\n");
      const date = formatDate(new Date(), "yyyyMMdd_HHmm");
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `TransactionsStakeToken_${date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>
<style lang="scss">
.modal-body-scrollable {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
